import React, { useMemo } from 'react';
import { Agent } from '../../../domain/agents/models/Agent';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { selectSelectedMessages } from '../../../domain/selectors/selected/chat/selectSelectedMessages';
import { selectSelectedSession } from '../../../domain/selectors/selected/chat/selectSelectedSession';
import { resolveSessionAgent } from '../../../domain/resolvers/resolveSessionAgent';
import { useAppSelector } from '../../../redux/hooks';

// TODO: fix up suggested prompt option and suggested prompt category option once we organize conversation starters and prompt library
export interface SuggestedPromptOption {
    id: string;
    title: string;
    text: string;
    requiresDocument: boolean;
    children?: SuggestedPromptOption[];
}

export interface SuggestedPromptCategoryOption {
    id: string;
    title: string;
    requiresDocument?: boolean;
    items: SuggestedPromptOption[];
}

export interface UseSuggestedPromptsVm {
    isSuggestedPromptsOpen: boolean;
    isDropUp: boolean;
    shouldExpandSuggestedPromptCategoriesByDefault: boolean;
    suggestedPromptCategories: SuggestedPromptCategoryOption[];
    onToggleSuggestedPromptsOpen: () => void;
    onCloseSuggestedPrompts: () => void;
}

interface UseSuggestedPromptsProps {
    agentsById: Record<string, Agent>;
}

export const useSuggestedPrompts = ({ agentsById }: UseSuggestedPromptsProps): UseSuggestedPromptsVm => {
    const session = useAppSelector(selectSelectedSession);
    const selectedMessages = useAppSelector(selectSelectedMessages);
    const [isSuggestedPromptsOpen, setIsSuggestedPromptsOpen] = React.useState(false);

    const resolvedSessionAgent = React.useMemo(
        () => (session ? resolveSessionAgent(session, agentsById) : null),
        [agentsById, session],
    );

    const isDropUp = selectedMessages.length > 0;

    const suggestedPromptCategories = useMemo<SuggestedPromptCategoryOption[]>(() => {
		if (!resolvedSessionAgent) {
			return [];
		}

		return resolvedSessionAgent.conversationStarters
			.map((group, groupIndex) => {
				const items = group.items.map((item, itemIndex) => ({
					id: `group-${groupIndex}-item-${itemIndex}`,
					title: item.title?.trim() || item.prompt,
					text: item.prompt,
					requiresDocument: item.requiresDocument === true,
					children: (item.followUps ?? []).map((followUpPrompt, childIndex) => ({
						id: `group-${groupIndex}-item-${itemIndex}-child-${childIndex}`,
						title: followUpPrompt,
						text: followUpPrompt,
						requiresDocument: item.requiresDocument === true,
					})),
				}));

				return {
					id: `group-${groupIndex}`,
					title: group.category,
					requiresDocument: items.length > 0 && items.every((item) => item.requiresDocument),
					items,
				};
			})
			.filter((category) => category.items.length > 0);
	}, [resolvedSessionAgent]);

    const shouldExpandSuggestedPromptCategoriesByDefault = session?.providerType !== ChatProviderType.ProGPT;

    const onToggleSuggestedPromptsOpen = React.useCallback((): void => {
        setIsSuggestedPromptsOpen((prev) => !prev);
    }, []);

    const onCloseSuggestedPrompts = React.useCallback((): void => {
        setIsSuggestedPromptsOpen(false);
    }, []);

    return {
        isSuggestedPromptsOpen,
        isDropUp,
        shouldExpandSuggestedPromptCategoriesByDefault,
        suggestedPromptCategories,
        onToggleSuggestedPromptsOpen,
        onCloseSuggestedPrompts,
    };
};

export default useSuggestedPrompts;
